// src/pages/ManageTeam.tsx
// Firm admin tool for managing two things:
// 1. Team Members (firm_members): people with login access to this firm on Altveno
// 2. Public Bios (team_members): people displayed on the firm's public profile (may or may not have login)
//
// Public profile display merges:
//  - team_members rows (external bios, with photo upload)
//  - firm_members where show_publicly=true (logged-in users, pulls profile bio/title/avatar)

import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://vsoomqfayfmtbnulnugn.supabase.co';

interface FirmMember {
  id: string;
  firm_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  added_at: string;
  added_by: string | null;
  removed_at: string | null;
  show_publicly: boolean;
  display_order: number | null;
  profile?: {
    id: string;
    email: string;
    full_name: string;
    avatar_url: string | null;
    bio: string | null;
    title: string | null;
    account_status: string;
  };
}

interface PublicBio {
  id: string;
  firm_id: string;
  name: string;
  title: string | null;
  bio: string | null;
  photo_url: string | null;
  display_order: number | null;
}

interface PendingInvitation {
  id: string;
  email: string;
  role_intended: string;
  firm_id: string | null;
  sent_at: string;
  status: string;
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '24px 32px', minHeight: '100vh', backgroundColor: '#F5F6F8' },
  header: { fontSize: 24, fontWeight: 400, fontFamily: 'Georgia, serif', color: '#0A0A0A', marginBottom: 4 },
  subheader: { fontSize: 13, color: '#6B7280', marginBottom: 32 },
  sectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 16, marginTop: 32, gap: 16,
  },
  sectionTitle: { fontSize: 18, fontWeight: 400, fontFamily: 'Georgia, serif', color: '#0A0A0A' },
  sectionDesc: { fontSize: 12, color: '#6B7280', marginTop: 2 },
  card: {
    backgroundColor: '#FFFFFF', border: '1px solid #D0D5DD',
    borderRadius: 4, padding: 0, marginBottom: 12, overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  row: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: 16, borderBottom: '1px solid #E8EBF0',
  },
  rowName: { fontSize: 14, color: '#0A0A0A', fontWeight: 500 },
  rowMeta: { fontSize: 11, color: '#6B7280', marginTop: 2 },
  avatar: {
    width: 40, height: 40, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 600, color: '#FFFFFF', flexShrink: 0,
    overflow: 'hidden',
  },
  badge: {
    fontSize: 10, padding: '3px 8px', borderRadius: 4,
    fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.4,
  },
  primaryBtn: {
    backgroundColor: '#0A1628', color: '#FFFFFF', border: 'none',
    padding: '10px 18px', borderRadius: 3, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  secondaryBtn: {
    backgroundColor: '#FFFFFF', color: '#3D4552',
    border: '1px solid #D0D5DD', borderRadius: 3,
    padding: '6px 12px', fontSize: 11, cursor: 'pointer', marginLeft: 6,
  },
  dangerBtn: {
    backgroundColor: '#FFFFFF', color: '#C0222A',
    border: '1px solid #D0D5DD', borderRadius: 3,
    padding: '6px 12px', fontSize: 11, cursor: 'pointer', marginLeft: 6,
  },
  toggle: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '6px 10px', borderRadius: 3, fontSize: 11, cursor: 'pointer',
    backgroundColor: '#F5F6F8', border: '1px solid #D0D5DD',
    marginLeft: 6, color: '#6B7280',
  },
  emptyText: { fontSize: 13, color: '#6B7280', textAlign: 'center' as const, padding: 32 },
  modalOverlay: {
    position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(10,22,40,0.6)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
  },
  modal: {
    backgroundColor: '#FFFFFF', border: '1px solid #D0D5DD',
    borderRadius: 4, padding: 28, width: 560, maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  modalTitle: { fontSize: 20, fontWeight: 400, fontFamily: 'Georgia, serif', color: '#0A0A0A', marginBottom: 4 },
  modalSubtitle: { fontSize: 13, color: '#6B7280', marginBottom: 20 },
  label: {
    fontSize: 11, color: '#6B7280', marginBottom: 6,
    letterSpacing: 0.5, textTransform: 'uppercase' as const,
  },
  input: {
    width: '100%', padding: 10, borderRadius: 3,
    backgroundColor: '#FFFFFF', color: '#0A0A0A',
    border: '1px solid #D0D5DD', fontSize: 13,
    marginBottom: 14, boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', padding: 12, borderRadius: 3,
    backgroundColor: '#FFFFFF', color: '#0A0A0A',
    border: '1px solid #D0D5DD', fontSize: 13,
    fontFamily: 'inherit', marginBottom: 14, minHeight: 100,
    boxSizing: 'border-box' as const, resize: 'vertical' as const,
  },
  noAccessCard: {
    backgroundColor: '#FFFFFF', border: '1px solid #D0D5DD',
    borderRadius: 4, padding: 40, textAlign: 'center' as const,
    maxWidth: 480, margin: '40px auto',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
};

const roleBadge = (role: string): React.CSSProperties => {
  if (role === 'owner') return { ...styles.badge, backgroundColor: '#C9A84C15', color: '#C9A84C' };
  if (role === 'admin') return { ...styles.badge, backgroundColor: '#0A162815', color: '#0A1628' };
  return { ...styles.badge, backgroundColor: '#6B728015', color: '#6B7280' };
};

export default function ManageTeam() {
  const { profile: myProfile, firm, loading: profileLoading } = useProfile();
  const [isFirmAdmin, setIsFirmAdmin] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  const [members, setMembers] = useState<FirmMember[]>([]);
  const [bios, setBios] = useState<PublicBio[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showBioModal, setShowBioModal] = useState(false);
  const [editingBio, setEditingBio] = useState<PublicBio | null>(null);

  // Add member form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteResult, setInviteResult] = useState<{ success: boolean; message: string } | null>(null);

  // Public bio form
  const [bioName, setBioName] = useState('');
  const [bioTitle, setBioTitle] = useState('');
  const [bioText, setBioText] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);

  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!profileLoading && myProfile && firm) {
      checkAdminAccess();
    } else if (!profileLoading && (!myProfile || !firm)) {
      setCheckingAccess(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, myProfile?.id, firm?.id]);

  const checkAdminAccess = async () => {
    if (!firm || !myProfile) return;
    const { data } = await supabase
      .from('firm_members')
      .select('role')
      .eq('firm_id', firm.id)
      .eq('user_id', myProfile.id)
      .is('removed_at', null)
      .maybeSingle();
    
    const isAdmin = (data?.role === 'admin' || data?.role === 'owner') ||
                    (firm && (firm as any).owner_id === myProfile.id);
    setIsFirmAdmin(isAdmin);
    setCheckingAccess(false);
    if (isAdmin) await loadAll();
  };

  const loadAll = async () => {
    if (!firm) return;
    setLoading(true);

    const [membersRes, biosRes, invitesRes] = await Promise.all([
      supabase
        .from('firm_members')
        .select('*, profile:profiles!firm_members_user_id_fkey(id, email, full_name, avatar_url, bio, title, account_status)')
        .eq('firm_id', firm.id)
        .is('removed_at', null)
        .order('added_at'),
      supabase
        .from('team_members')
        .select('*')
        .eq('firm_id', firm.id)
        .order('display_order', { nullsFirst: false })
        .order('id'),
      supabase
        .from('invitations')
        .select('id, email, role_intended, firm_id, sent_at, status')
        .eq('firm_id', firm.id)
        .in('status', ['sent', 'opened'])
        .order('sent_at', { ascending: false }),
    ]);

    setMembers((membersRes.data || []) as any);
    setBios((biosRes.data || []) as any);
    setPendingInvites((invitesRes.data || []) as any);
    setLoading(false);
  };

  // ===== INVITE MEMBER =====
  const handleSendInvite = async () => {
    if (!firm || !inviteEmail.trim()) return;
    setActionLoading(true);
    setInviteResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-invitation-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role_intended: myProfile?.role || 'manager',
          firm_id: firm.id,
          firm_name_suggested: firm.name,
          custom_message: inviteMessage.trim() || null,
        }),
      });

      const json = await res.json();
      if (!res.ok) {
        setInviteResult({ success: false, message: json.error || 'Failed to send invitation' });
      } else {
        setInviteResult({ success: true, message: `Invitation sent to ${inviteEmail}.` });
        await loadAll();
      }
    } catch (err: any) {
      setInviteResult({ success: false, message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async (member: FirmMember) => {
    if (member.role === 'owner') {
      alert('Cannot remove the firm owner.');
      return;
    }
    if (member.user_id === myProfile?.id) {
      alert('You cannot remove yourself from your own firm.');
      return;
    }
    if (!window.confirm(`Remove ${member.profile?.full_name || member.profile?.email} from the firm? They will lose access.`)) return;
    
    setActionLoading(true);
    const { error } = await supabase
      .from('firm_members')
      .update({ removed_at: new Date().toISOString() })
      .eq('id', member.id);
    setActionLoading(false);
    if (error) { alert(`Remove failed: ${error.message}`); return; }
    await loadAll();
  };

  const handleChangeRole = async (member: FirmMember, newRole: 'admin' | 'member') => {
    if (member.role === 'owner') {
      alert('Cannot change the owner role.');
      return;
    }
    setActionLoading(true);
    const { error } = await supabase
      .from('firm_members')
      .update({ role: newRole })
      .eq('id', member.id);
    setActionLoading(false);
    if (error) { alert(`Role change failed: ${error.message}`); return; }
    await loadAll();
  };

  const handleToggleShowPublicly = async (member: FirmMember) => {
    setActionLoading(true);
    const { error } = await supabase
      .from('firm_members')
      .update({ show_publicly: !member.show_publicly })
      .eq('id', member.id);
    setActionLoading(false);
    if (error) { alert(`Update failed: ${error.message}`); return; }
    await loadAll();
  };

  const handleRevokeInvite = async (invite: PendingInvitation) => {
    if (!window.confirm(`Revoke invitation to ${invite.email}?`)) return;
    setActionLoading(true);
    const { error } = await supabase
      .from('invitations')
      .update({ status: 'revoked' })
      .eq('id', invite.id);
    setActionLoading(false);
    if (error) { alert(`Revoke failed: ${error.message}`); return; }
    await loadAll();
  };

  // ===== PUBLIC BIO CRUD (team_members table) =====
  const openBioModal = (existing?: PublicBio) => {
    if (existing) {
      setEditingBio(existing);
      setBioName(existing.name);
      setBioTitle(existing.title || '');
      setBioText(existing.bio || '');
      setExistingPhotoUrl(existing.photo_url || '');
    } else {
      setEditingBio(null);
      setBioName('');
      setBioTitle('');
      setBioText('');
      setExistingPhotoUrl('');
    }
    setPhotoFile(null);
    setPhotoPreview(null);
    setShowBioModal(true);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSaveBio = async () => {
    if (!firm || !bioName.trim()) return;
    setActionLoading(true);

    let photoUrl = existingPhotoUrl;
    if (photoFile) {
      const ext = photoFile.name.split('.').pop();
      const path = `${firm.id}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('team-photos').upload(path, photoFile);
      if (uploadError) {
        alert('Photo upload failed: ' + uploadError.message);
        setActionLoading(false);
        return;
      }
      const { data: urlData } = supabase.storage.from('team-photos').getPublicUrl(path);
      photoUrl = urlData.publicUrl;
    }

    if (editingBio) {
      const { error } = await supabase
        .from('team_members')
        .update({
          name: bioName.trim(),
          title: bioTitle.trim() || null,
          bio: bioText.trim() || null,
          photo_url: photoUrl || null,
        })
        .eq('id', editingBio.id);
      setActionLoading(false);
      if (error) { alert(`Save failed: ${error.message}`); return; }
    } else {
      const { error } = await supabase
        .from('team_members')
        .insert({
          firm_id: firm.id,
          name: bioName.trim(),
          title: bioTitle.trim() || null,
          bio: bioText.trim() || null,
          photo_url: photoUrl || null,
          display_order: bios.length,
        });
      setActionLoading(false);
      if (error) { alert(`Save failed: ${error.message}`); return; }
    }

    setShowBioModal(false);
    setPhotoFile(null);
    setPhotoPreview(null);
    await loadAll();
  };

  const handleDeleteBio = async (bio: PublicBio) => {
    if (!window.confirm(`Delete bio for "${bio.name}"?`)) return;
    setActionLoading(true);
    const { error } = await supabase.from('team_members').delete().eq('id', bio.id);
    setActionLoading(false);
    if (error) { alert(`Delete failed: ${error.message}`); return; }
    await loadAll();
  };

  const closeModals = () => {
    setShowAddMemberModal(false);
    setShowBioModal(false);
    setEditingBio(null);
    setInviteEmail('');
    setInviteMessage('');
    setInviteResult(null);
    setBioName('');
    setBioTitle('');
    setBioText('');
    setPhotoFile(null);
    setPhotoPreview(null);
    setExistingPhotoUrl('');
  };

  const getInitials = (name?: string | null) => {
    if (!name) return '?';
    return name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (profileLoading || checkingAccess) {
    return <div style={styles.container}><div style={styles.emptyText}>Loading...</div></div>;
  }

  if (!firm) {
    return (
      <div style={styles.container}>
        <div style={styles.noAccessCard}>
          <div style={{ fontSize: 18, fontWeight: 400, fontFamily: 'Georgia, serif', color: '#0A0A0A', marginBottom: 8 }}>No firm</div>
          <div style={{ fontSize: 13, color: '#6B7280' }}>
            You need to be associated with a firm to manage a team.
          </div>
        </div>
      </div>
    );
  }

  if (!isFirmAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.noAccessCard}>
          <div style={{ fontSize: 18, fontWeight: 400, fontFamily: 'Georgia, serif', color: '#0A0A0A', marginBottom: 8 }}>Admin access required</div>
          <div style={{ fontSize: 13, color: '#6B7280' }}>
            Only firm admins can manage team members. Contact your firm admin to make changes.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>Manage Team</div>
      <div style={styles.subheader}>
        {firm.name} · Manage who has platform access and who appears on your public firm profile.
      </div>

      {/* ===== SECTION 1: TEAM MEMBERS ===== */}
      <div style={styles.sectionHeader}>
        <div>
          <div style={styles.sectionTitle}>Team members</div>
          <div style={styles.sectionDesc}>People with login access to {firm.name} on Altveno</div>
        </div>
        <button style={styles.primaryBtn} onClick={() => setShowAddMemberModal(true)}>
          + Invite Team Member
        </button>
      </div>

      <div style={styles.card}>
        {loading ? <div style={styles.emptyText}>Loading...</div>
        : members.length === 0 ? <div style={styles.emptyText}>No team members yet.</div>
        : members.map((m) => {
          const isMe = m.user_id === myProfile?.id;
          return (
            <div key={m.id} style={styles.row}>
              <div style={{
                ...styles.avatar,
                background: m.profile?.avatar_url ? 'transparent' : 'linear-gradient(135deg, #0A1628, #C9A84C)',
              }}>
                {m.profile?.avatar_url ? (
                  <img src={m.profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  getInitials(m.profile?.full_name)
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.rowName}>
                  {m.profile?.full_name || 'Unknown'} {isMe && <span style={{ fontSize: 11, color: '#6B7280' }}>(you)</span>}
                </div>
                <div style={styles.rowMeta}>
                  {m.profile?.email}
                  {m.profile?.title && <> · {m.profile.title}</>}
                  {' · '}Added {new Date(m.added_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </div>
              <div style={roleBadge(m.role)}>{m.role}</div>
              <div
                style={{
                  ...styles.toggle,
                  color: m.show_publicly ? '#0A7C42' : '#6B7280',
                  borderColor: m.show_publicly ? '#0A7C42' : '#D0D5DD',
                }}
                onClick={() => handleToggleShowPublicly(m)}
                title={m.show_publicly ? 'Visible on public firm profile' : 'Hidden from public firm profile'}
              >
                {m.show_publicly ? '✓ Public' : 'Private'}
              </div>
              {m.role !== 'owner' && !isMe && (
                <>
                  {m.role === 'admin' ? (
                    <button style={styles.secondaryBtn} onClick={() => handleChangeRole(m, 'member')} disabled={actionLoading}>
                      Make Member
                    </button>
                  ) : (
                    <button style={styles.secondaryBtn} onClick={() => handleChangeRole(m, 'admin')} disabled={actionLoading}>
                      Make Admin
                    </button>
                  )}
                  <button style={styles.dangerBtn} onClick={() => handleRemoveMember(m)} disabled={actionLoading}>
                    Remove
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Pending invitations */}
      {pendingInvites.length > 0 && (
        <>
          <div style={{ ...styles.sectionHeader, marginTop: 16 }}>
            <div>
              <div style={{ ...styles.sectionTitle, fontSize: 14 }}>Pending invitations</div>
            </div>
          </div>
          <div style={styles.card}>
            {pendingInvites.map((inv) => (
              <div key={inv.id} style={styles.row}>
                <div style={{
                  ...styles.avatar,
                  background: '#92640A15', color: '#92640A', fontSize: 16,
                }}>✉</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.rowName}>{inv.email}</div>
                  <div style={styles.rowMeta}>
                    Invited {new Date(inv.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    {' · '}Status: {inv.status}
                  </div>
                </div>
                <button style={styles.dangerBtn} onClick={() => handleRevokeInvite(inv)}>
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ===== SECTION 2: PUBLIC BIOS ===== */}
      <div style={styles.sectionHeader}>
        <div>
          <div style={styles.sectionTitle}>Public firm bios</div>
          <div style={styles.sectionDesc}>
            Key people shown on your public firm profile. Team members above with "Public" enabled also appear here automatically.
          </div>
        </div>
        <button style={styles.primaryBtn} onClick={() => openBioModal()}>
          + Add Bio
        </button>
      </div>

      <div style={styles.card}>
        {members.filter((m) => m.show_publicly && m.profile).length === 0 && bios.length === 0 ? (
          <div style={styles.emptyText}>
            No public bios yet. Mark team members as "Public" above, or add external bios here.
          </div>
        ) : (
          <>
            {/* Logged-in team members marked public */}
            {members.filter((m) => m.show_publicly).map((m) => (
              <div key={`member-${m.id}`} style={styles.row}>
                <div style={{
                  ...styles.avatar,
                  background: m.profile?.avatar_url ? 'transparent' : 'linear-gradient(135deg, #0A1628, #C9A84C)',
                }}>
                  {m.profile?.avatar_url ? (
                    <img src={m.profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    getInitials(m.profile?.full_name)
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.rowName}>{m.profile?.full_name}</div>
                  <div style={styles.rowMeta}>
                    {m.profile?.title || <span style={{ fontStyle: 'italic' }}>(no title set, edit in Settings)</span>}
                    {' · '}<span style={{ color: '#0D6E8A' }}>From team member</span>
                  </div>
                  {m.profile?.bio && (
                    <div style={{ ...styles.rowMeta, marginTop: 6, color: '#6B7280' }}>{m.profile.bio}</div>
                  )}
                </div>
              </div>
            ))}

            {/* External bios from team_members */}
            {bios.map((b) => (
              <div key={`bio-${b.id}`} style={styles.row}>
                <div style={{
                  ...styles.avatar,
                  background: b.photo_url ? 'transparent' : 'linear-gradient(135deg, #C9A84C, #92640A)',
                }}>
                  {b.photo_url ? (
                    <img src={b.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    getInitials(b.name)
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.rowName}>{b.name}</div>
                  <div style={styles.rowMeta}>
                    {b.title || <span style={{ fontStyle: 'italic' }}>(no title)</span>}
                    {' · '}<span style={{ color: '#92640A' }}>External bio</span>
                  </div>
                  {b.bio && (
                    <div style={{ ...styles.rowMeta, marginTop: 6, color: '#6B7280' }}>{b.bio}</div>
                  )}
                </div>
                <button style={styles.secondaryBtn} onClick={() => openBioModal(b)}>Edit</button>
                <button style={styles.dangerBtn} onClick={() => handleDeleteBio(b)}>Delete</button>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ===== INVITE MEMBER MODAL ===== */}
      {showAddMemberModal && (
        <div style={styles.modalOverlay} onClick={closeModals}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalTitle}>Invite Team Member</div>
            <div style={styles.modalSubtitle}>
              Send an invitation email to add someone to {firm.name}. They'll create their account and join your firm automatically.
            </div>

            <div style={styles.label}>Email *</div>
            <input
              type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@yourfirm.com" style={styles.input} autoFocus
            />

            <div style={styles.label}>Role</div>
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as any)} style={styles.input}>
              <option value="member">Member (can do everything except manage team)</option>
              <option value="admin">Admin (full access including team management)</option>
            </select>

            <div style={styles.label}>Personal message (optional)</div>
            <textarea
              value={inviteMessage} onChange={(e) => setInviteMessage(e.target.value)}
              placeholder="e.g. Hi Alex, here's the platform access I mentioned. Looking forward to working together."
              style={styles.textarea}
            />

            {inviteResult && (
              <div style={{
                padding: 12, borderRadius: 3, marginBottom: 14,
                background: inviteResult.success ? '#0A7C4215' : '#C0222A15',
                color: inviteResult.success ? '#0A7C42' : '#C0222A',
                fontSize: 13,
              }}>
                {inviteResult.message}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{ ...styles.primaryBtn, opacity: (actionLoading || !inviteEmail) ? 0.5 : 1 }}
                onClick={handleSendInvite} disabled={actionLoading || !inviteEmail}
              >
                {actionLoading ? 'Sending...' : 'Send Invitation'}
              </button>
              <button
                style={{ ...styles.secondaryBtn, marginLeft: 0, padding: '10px 16px', fontSize: 13 }}
                onClick={closeModals} disabled={actionLoading}
              >
                {inviteResult?.success ? 'Done' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== BIO MODAL ===== */}
      {showBioModal && (
        <div style={styles.modalOverlay} onClick={closeModals}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalTitle}>{editingBio ? 'Edit bio' : 'Add public bio'}</div>
            <div style={styles.modalSubtitle}>
              For people who appear on your firm profile but don't have Altveno login access (chairmen, advisors, founders).
            </div>

            <div style={styles.label}>Photo (optional)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              {photoPreview ? (
                <img src={photoPreview} alt="preview" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
              ) : existingPhotoUrl ? (
                <img src={existingPhotoUrl} alt="current" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: '#F5F6F8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, color: '#6B7280',
                }}>👤</div>
              )}
              <button
                onClick={() => fileRef.current?.click()}
                style={{ ...styles.secondaryBtn, marginLeft: 0, padding: '8px 14px', fontSize: 12 }}
              >
                {photoPreview || existingPhotoUrl ? 'Change photo' : 'Upload photo'}
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
            </div>

            <div style={styles.label}>Full name *</div>
            <input type="text" value={bioName} onChange={(e) => setBioName(e.target.value)}
              placeholder="e.g. John Smith" style={styles.input} autoFocus />

            <div style={styles.label}>Title</div>
            <input type="text" value={bioTitle} onChange={(e) => setBioTitle(e.target.value)}
              placeholder="e.g. Chairman" style={styles.input} />

            <div style={styles.label}>Bio</div>
            <textarea value={bioText} onChange={(e) => setBioText(e.target.value)}
              placeholder="e.g. 20 years experience in alternative investments. Previously at Goldman Sachs and Bridgewater."
              style={styles.textarea} />

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{ ...styles.primaryBtn, opacity: (actionLoading || !bioName.trim()) ? 0.5 : 1 }}
                onClick={handleSaveBio} disabled={actionLoading || !bioName.trim()}
              >
                {actionLoading ? 'Saving...' : (editingBio ? 'Save changes' : 'Add bio')}
              </button>
              <button
                style={{ ...styles.secondaryBtn, marginLeft: 0, padding: '10px 16px', fontSize: 13 }}
                onClick={closeModals} disabled={actionLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}